"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { postsAPI, channelsAPI, socialUtils, handleApiError } from "../../services/api";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { showErrorToast } from "../../utils/notificationUtils";
import ChannelPostCreation from "./ChannelPostCreation";
import ChannelPostFeed from "./ChannelPostFeed";
import { resolveImageUrl, withPlaceholder } from "../../utils/imageUtils";
import ChannelHeader from "./ChannelHeader";
import ChannelSettings from "./ChannelSettings";
import ManageMembersModal from "./modals/ManageMembersModal";
import ManageJoinRequestsModal from "./modals/ManageJoinRequestsModal";
import InviteMembersModal from "./modals/InviteMembersModal";
import ChannelEventsModal from "./modals/ChannelEventsModal";
import ChannelRulesModal from "./modals/ChannelRulesModal";
import JoinRequestModal from "./modals/JoinRequestModal";
import { Pin, Calendar } from "lucide-react";
import ChannelDetailSkeleton from "./ChannelDetailSkeleton";
import PostModal from "../modals/PostModal";
import DeleteConfirmationModal from "../modals/DeleteConfirmation";
import { usePostUploadActions } from "../../contexts/PostUploadContext";
import "./ChannelDetail.css";

// Helper to normalize IDs for consistent comparison (handles strings, numbers, and objects)
const normalizeId = (id) => {
  if (id === null || id === undefined) return null;
  return typeof id === 'object' && id.id ? String(id.id) : String(id);
};

const ChannelDetail = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { getUserType, getProfileImageUrl, user } = useAuth();
  const { startUpdate } = usePostUploadActions();

  // Check if user came from profile page and which tab
  const fromProfile = location.state?.fromProfile || false;
  const fromProfileTab = location.state?.fromProfileTab || null;
  const profileId = location.state?.profileId || null; // For other users' profiles
  
  // Determine the back navigation path
  const getBackPath = () => {
    if (!fromProfile) return "/social/channels";
    // If coming from communities tab (tab 3)
    if (fromProfileTab === 3) {
      // If viewing another user's profile, use their profile ID
      if (profileId) return `/social/profile/${profileId}/communities`;
      // Otherwise navigate to own profile communities tab
      return `/social/profile/communities`;
    }
    // Otherwise navigate to profile (or other user's profile)
    if (profileId) return `/social/profile/${profileId}`;
    return `/social/profile`;
  };

  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [userRole, setUserRole] = useState(null); // owner, admin, member
  const [isJoined, setIsJoined] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const headerContainerRef = useRef(null);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [showSidebarDrawer, setShowSidebarDrawer] = useState(false);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  const [showChannelJoinRequestModal, setShowChannelJoinRequestModal] = useState(false);
  const [joinRequestStatus, setJoinRequestStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Edit/Delete Post State
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  // Request guards to prevent duplicate/redundant calls
  const isRequestInFlightRef = useRef(false);
  const lastRequestDepsRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Intersection Observer for sticky header transition
  useEffect(() => {
    const header = headerContainerRef.current;
    if (!header) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only set scrolled when header is substantially out of view
        setIsHeaderScrolled(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: "-200px 0px 0px 0px" // Only trigger when header is mostly gone
      }
    );

    observer.observe(header);
    return () => observer.disconnect();
  }, [isLoading]);

  useEffect(() => {
    const currentUserId = user?.id || user?.user_id;
    if (!channelId) return;

    // Check if dependencies are the same as last started request to prevent redundant calls
    const currentDeps = JSON.stringify([channelId, currentUserId, refreshTrigger]);
    
    // If request is in flight and deps haven't changed, skip
    if (isRequestInFlightRef.current && lastRequestDepsRef.current === currentDeps) {
      return;
    }

    // Set lock IMMEDIATELY (synchronously)
    isRequestInFlightRef.current = true;
    lastRequestDepsRef.current = currentDeps;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let cancelled = false;

    const loadChannelData = async () => {
      setIsLoading(true);
      try {
        if (cancelled || abortController.signal.aborted) return;

        const channelResponse = await channelsAPI.getChannel(channelId);
        
        if (cancelled || abortController.signal.aborted) return;

        // Also load members with role info so roles persist correctly after refresh
        let membersWithRoles = null;
        try {
          const membersResponse = await channelsAPI.getChannelMembers(channelId, {
            page: 1,
            per_page: 200,
          });
          if (membersResponse?.data?.data) {
            membersWithRoles = membersResponse.data.data;
          }
        } catch (e) {
          // If members endpoint fails, we still fall back to channel response data
          console.error("Failed to load channel members with roles", e);
        }

        if (cancelled || abortController.signal.aborted) return;

        const postsResponse = await postsAPI.getPosts({
          channel_id: channelId,
          page: 1,
          perPage: 20
        });

        if (cancelled || abortController.signal.aborted) return;

        if (channelResponse.data && channelResponse.data.data) {
          const channelData = channelResponse.data.data;
          const currentUserId = user?.id || user?.user_id;
          const ownerId = channelData.owner?.id ?? channelData.owner?.user_id ?? channelData.owner_id;
          const isOwner = currentUserId && ownerId && Number(ownerId) === Number(currentUserId);
          const userRoleFromChannel =
            channelData.user_role || channelData.userRole || (isOwner ? "owner" : null);

          // Prefer role from members endpoint if available
          let userRoleFromMembers = null;
          if (Array.isArray(membersWithRoles) && currentUserId) {
            const selfMember = membersWithRoles.find((member) => {
              const memberUser = member.user || member;
              const memberId =
                memberUser?.id ??
                memberUser?.user_id ??
                member.user_id ??
                member.id ??
                null;
              return memberId && Number(memberId) === Number(currentUserId);
            });
            if (selfMember?.role) {
              userRoleFromMembers = String(selfMember.role).toLowerCase();
            }
          }

          const finalUserRole = isOwner
            ? "owner"
            : userRoleFromMembers || (userRoleFromChannel ? String(userRoleFromChannel).toLowerCase() : null);

          const isAdmin = finalUserRole === "admin" || channelData.admins?.some(admin => {
            const adminId = admin.id ?? admin.user_id;
            return currentUserId && Number(adminId) === Number(currentUserId);
          });

          const membersArray =
            channelData.members ||
            channelData.channel_members ||
            channelData.memberships ||
            [];
          const isMember = Array.isArray(membersArray)
            ? membersArray.some(member => {
              const memberUser = member.user || member;
              const memberId = memberUser?.id ?? member.user_id ?? member.id;
              return currentUserId && Number(memberId) === Number(currentUserId);
            })
            : false;

          const apiIsJoined =
            channelData.is_joined ??
            channelData.isJoined ??
            channelData.joined ??
            channelData.is_member;
          const derivedIsJoined =
            apiIsJoined !== undefined
              ? Boolean(apiIsJoined)
              : isOwner || isAdmin || isMember || Boolean(finalUserRole);

          const memberCount = channelData.members_count ?? channelData.memberCount ?? channelData.members?.length ?? 0;

          const profileImageUrl = resolveImageUrl(
            channelData.profile_image_url,
            channelData.profile_image,
            channelData.profileImageUrl,
            channelData.profileImage,
            channelData.profile_image_path,
            channelData.images?.profile,
            channelData.media?.profile
          );

          const normalizeMember = (member, fallbackRole = "Member") => {
            if (!member) return null;
            const memberUser = member.user || member;
            const memberId =
              memberUser?.id ??
              memberUser?.user_id ??
              member.user_id ??
              member.id ??
              null;
            const displayName =
              memberUser?.name ||
              (memberUser?.first_name && memberUser?.last_name
                ? `${memberUser.first_name} ${memberUser.last_name}`
                : null) ||
              "Community member";

            const avatar = withPlaceholder(
              resolveImageUrl(
                memberUser?.profile_image_url,
                memberUser?.profile_image,
                memberUser?.avatar,
                memberUser?.profileImage,
                memberUser?.profile_image_path,
                member.profile_image_url
              ),
              "/icons/person.png"
            );

            return {
              id: memberId || displayName,
              name: displayName,
              profileImage: avatar,
              role:
                member.role ||
                memberUser?.role ||
                memberUser?.pivot?.role ||
                fallbackRole,
            };
          };

          const ownerDisplay = normalizeMember(channelData.owner, "Owner");
          if (ownerDisplay) {
            ownerDisplay.role = "Owner";
          }

          const adminDisplays = (channelData.admins || [])
            .map(admin => normalizeMember(admin, "Admin"))
            .filter(Boolean)
            .map(admin => ({ ...admin, role: "Admin" }));

          const memberDisplays = Array.isArray(membersArray)
            ? membersArray
              .map(member =>
                normalizeMember(
                  member,
                  member?.pivot?.role || member?.role || "Member"
                )
              )
              .filter(Boolean)
            : [];

          const collectedMembers = [];
          const seenMemberIds = new Set();
          const pushMember = (entry) => {
            if (!entry) return;
            const key = entry.id || entry.username;
            if (!key || seenMemberIds.has(key)) return;
            seenMemberIds.add(key);
            collectedMembers.push(entry);
          };

          if (ownerDisplay) pushMember(ownerDisplay);
          adminDisplays.forEach(pushMember);
          memberDisplays.forEach(pushMember);

          const rulesSource =
            channelData.rules ||
            channelData.channel_rules ||
            channelData.guidelines ||
            channelData.channel_guidelines ||
            null;

          let normalizedRules = [];
          if (Array.isArray(rulesSource)) {
            normalizedRules = rulesSource
              .map(rule => {
                if (typeof rule === "string") return rule.trim();
                if (rule?.text) return String(rule.text).trim();
                return null;
              })
              .filter(Boolean)
              .slice(0, 6);
          } else if (typeof rulesSource === "string") {
            normalizedRules = rulesSource
              .split(/\n+/)
              .map(rule => rule.trim())
              .filter(Boolean)
              .slice(0, 6);
          }

          const rawEventsSource =
            channelData.upcoming_events ||
            channelData.upcomingEvents ||
            channelData.events ||
            [];

          const rawEvents = Array.isArray(rawEventsSource)
            ? rawEventsSource
            : (rawEventsSource && typeof rawEventsSource === 'object' && Array.isArray(rawEventsSource.data))
              ? rawEventsSource.data
              : [];

          const upcomingEvents = Array.isArray(rawEvents)
            ? rawEvents
              .map(event => {
                if (typeof event === "string") {
                  return {
                    id: event,
                    title: event,
                    date: null,
                    location: null,
                  };
                }

                const eventId =
                  event.id ||
                  event.event_id ||
                  event.uuid ||
                  event.slug ||
                  event.title ||
                  event.name;
                const eventTitle = event.title || event.name || "Channel event";
                const eventDate =
                  event.starts_at ||
                  event.start_at ||
                  event.start_time ||
                  event.date ||
                  event.scheduled_for ||
                  null;
                const eventLocation =
                  event.location ||
                  event.venue ||
                  event.place ||
                  null;

                return {
                  id: eventId,
                  title: eventTitle,
                  date: eventDate,
                  location: eventLocation,
                  description: event.description || null,
                };
              })
              .slice(0, 3)
            : [];

          const pinnedPostIds = (
            channelData.pinned_post_ids ||
            channelData.pinnedPostIds ||
            (Array.isArray(channelData.pinned_posts)
              ? channelData.pinned_posts.map(
                pinned =>
                  pinned.id ||
                  pinned.post_id ||
                  pinned.post?.id ||
                  pinned.post_id
              )
              : [])
          )
            .filter(Boolean)
            .map(id => Number(id) || id);

          const transformedChannel = {
            id: channelData.id,
            name: channelData.name,
            description: channelData.description,
            profileImage: withPlaceholder(profileImageUrl, "/images/social/pic-placeholder.png"),
            memberCount: (isOwner || isAdmin) && (!memberCount || memberCount < 1) ? 1 : memberCount,
            postCount: channelData.posts_count || channelData.postCount || 0,
            isPublic: (channelData.privacy_setting || channelData.privacySetting) === 'public',
            privacySetting: channelData.privacy_setting || channelData.privacySetting || 'public',
            owner: {
              id: channelData.owner?.id,
              name: channelData.owner?.name || (channelData.owner?.first_name && channelData.owner?.last_name ? `${channelData.owner.first_name} ${channelData.owner.last_name}` : null) || "Unknown",
              profileImage:
                withPlaceholder(
                  resolveImageUrl(
                    channelData.owner?.profile_image_url,
                    channelData.owner?.profile_image,
                    channelData.owner?.avatar,
                    channelData.owner?.profileImage,
                    channelData.owner?.profile_image_path
                  ),
                  "/icons/person.png"
                )
            },
            admins: (channelData.admins || []).map(admin => ({
              id: admin.id,
              name: admin.name || (admin.first_name && admin.last_name ? `${admin.first_name} ${admin.last_name}` : null) || "Unknown",
              profileImage:
                withPlaceholder(
                  resolveImageUrl(
                    admin.profile_image_url,
                    admin.profile_image,
                    admin.avatar,
                    admin.profile_image_path
                  ),
                  "/icons/person.png"
                )
            })),
            membersPreview: collectedMembers.slice(0, 8),
            rules: normalizedRules,
            upcomingEvents,
            pinnedPostIds,
            pinnedPosts: Array.isArray(channelData.pinned_posts)
              ? channelData.pinned_posts
                .map(socialUtils.transformPostData)
                .filter(Boolean)
                .map(post => ({
                  ...post,
                  channelId: parseInt(channelId),
                  author: {
                    id: post.author?.id || 0,
                    name: post.author?.username || post.author?.name || "Unknown User",
                    username: post.author?.username || `@user${post.author?.id || 0}`,
                    profileImage: post.author?.avatar || "/icons/person.png"
                  }
                }))
              : [],
          };

          if (cancelled || abortController.signal.aborted) return;
          setChannel(transformedChannel);

          setIsJoined(derivedIsJoined);
          setJoinRequestStatus(channelData.join_request_status || channelData.joinRequestStatus || null);
          setUserRole(finalUserRole || (derivedIsJoined ? "member" : null) || "viewer");
        }

        if (postsResponse.data && postsResponse.data.data) {
          const transformedPosts = postsResponse.data.data
            .map(socialUtils.transformPostData)
            .filter(post => post !== null)
            .map(post => ({
              ...post,
              channelId: parseInt(channelId),
              type: post.type || post.post_type || post.postType || "post",
              images: post.media ? post.media.map(m => m.url) : [],
              author: {
                id: post.author?.id || 0,
                name: post.author?.username || post.author?.name || "Unknown User",
                username: post.author?.username || `@user${post.author?.id || 0}`,
                profileImage: post.author?.avatar || "/icons/person.png"
              },
              createdAt: post.timestamp || post.created_at || "Just now",
              likes: post.reactions?.likes || post.likes || 0,
              comments: post.reactions?.comments || post.comments || 0,
              shares: post.reactions?.shares || post.shares || 0,
              isLiked: post.isLiked || false,
              created_at: post.created_at || new Date().toISOString()
            }));

          // Sort by created_at in descending order (newest first)
          transformedPosts.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
          });

          if (cancelled || abortController.signal.aborted) return;
          setPosts(transformedPosts);
          setChannel(prev =>
            prev
              ? {
                ...prev,
                postCount: transformedPosts.length
              }
              : prev
          );
        }
      } catch (error) {
        if (!cancelled && !abortController.signal.aborted) {
          handleApiError(error);
        }
      } finally {
        if (!cancelled && !abortController.signal.aborted) {
          setIsLoading(false);
          isRequestInFlightRef.current = false;
        }
      }
    };

    loadChannelData();

    return () => {
      cancelled = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [channelId, user?.id || user?.user_id, refreshTrigger]);

  // Fallback: Listen for channel-post-created event to reload posts
  useEffect(() => {
    if (!channelId) return;

    const handleChannelPostCreated = async (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (eventChannelId && Number(eventChannelId) === Number(channelId)) {
        // Reload posts for this channel
        try {
          const postsResponse = await postsAPI.getPosts({
            channel_id: channelId,
            page: 1,
            perPage: 20
          });

          if (postsResponse.data && postsResponse.data.data) {
            const transformedPosts = postsResponse.data.data
              .map(socialUtils.transformPostData)
              .filter(post => post !== null)
              .map(post => ({
                ...post,
                channelId: parseInt(channelId),
                type: post.type || post.post_type || post.postType || "post",
                images: post.media ? post.media.map(m => m.url) : [],
                author: {
                  id: post.author?.id || 0,
                  name: post.author?.username || post.author?.name || "Unknown User",
                  username: post.author?.username || `@user${post.author?.id || 0}`,
                  profileImage: post.author?.avatar || "/icons/person.png"
                },
                createdAt: post.timestamp || post.created_at || "Just now",
                likes: post.reactions?.likes || post.likes || 0,
                comments: post.reactions?.comments || post.comments || 0,
                shares: post.reactions?.shares || post.shares || 0,
                isLiked: post.isLiked || false,
                created_at: post.created_at || new Date().toISOString()
              }));

            // Sort by created_at in descending order (newest first)
            transformedPosts.sort((a, b) => {
              const dateA = new Date(a.created_at || a.createdAt || 0);
              const dateB = new Date(b.created_at || b.createdAt || 0);
              return dateB - dateA;
            });

            setPosts(transformedPosts);
            setChannel(prev =>
              prev
                ? {
                    ...prev,
                    postCount: transformedPosts.length
                  }
                : prev
            );
          }
        } catch (error) {
          console.error('[ChannelDetail] Error reloading posts:', error);
        }
      }
    };

    window.addEventListener("channel-post-created", handleChannelPostCreated);
    return () => {
      window.removeEventListener("channel-post-created", handleChannelPostCreated);
    };
  }, [channelId]);

  // Listen for background upload completion to update the feed in real-time
  useEffect(() => {
    const handlePostUploadCompleted = (event) => {
      const { post } = event.detail;
      if (!post) return;

      // Check channel_id in multiple possible formats
      const postChannelId = post.channel_id || post.channelId || post.channel?.id || post.channel?.channel_id;
      
      // More lenient check: if we're on a channel page and post was just created, accept it
      // This handles cases where channel_id might not be in the response
      const channelMatches = postChannelId && Number(postChannelId) === Number(channelId);
      const isRecentPost = post.created_at || post.createdAt || post.id;
      
      // Only add to channel if it's a post for THIS channel and post isn't already there
      // OR if it's a recent post (likely just created) and we're on the channel page
      if (channelMatches || (isRecentPost && !postChannelId)) {
        const transformedPost = socialUtils.transformPostData(post);
        if (!transformedPost) return;

        setPosts((prevPosts) => {
          // Check if post already exists
          const postId = transformedPost.id;
          if (prevPosts.some((p) => {
            const existingId = p.id || p.post_id || p.postId;
            return existingId && Number(existingId) === Number(postId);
          })) {
            return prevPosts;
          }
          
          // Add new post at the beginning and sort by created_at
          const updatedPosts = [transformedPost, ...prevPosts];
          return updatedPosts.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
          });
        });
        
        // Update channel post count
        setChannel(prev => prev ? { ...prev, postCount: (prev.postCount || 0) + 1 } : null);
      }
    };

    window.addEventListener("post-upload-completed", handlePostUploadCompleted);
    return () => {
      window.removeEventListener("post-upload-completed", handlePostUploadCompleted);
    };
  }, [channelId]);

  // Listen for background post update completion to refresh the local state
  useEffect(() => {
    const handlePostUpdateCompleted = (event) => {
      const { post, postId, postData } = event.detail;
      if (!post && !postId) return;

      const effectiveId = postId || post?.id;
      
      // Transform the raw server response to match our internal post structure
      const transformedPost = post ? socialUtils.transformPostData(post) : null;

      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          const currentId = p.id || p.post_id || p.postId;
          if (String(currentId) !== String(effectiveId)) return p;

          // Merge transformed server response with existing local data
          return {
            ...p,
            ...(transformedPost || {}),
            // Preserve specific fields if needed
            status: 'published'
          };
        })
      );
    };

    window.addEventListener("post-update-completed", handlePostUpdateCompleted);
    return () => {
      window.removeEventListener("post-update-completed", handlePostUpdateCompleted);
    };
  }, []);

  // Listen for channel joined/left events to keep UI in sync
  useEffect(() => {
    if (!channelId) return;

    const handleSync = (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (Number(eventChannelId) === Number(channelId)) {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('channel:joined', handleSync);
    window.addEventListener('channel:left', handleSync);
    window.addEventListener('channel:invitation-accepted', handleSync);

    const handleJoinRequestSync = (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (Number(eventChannelId) === Number(channelId)) {
        setJoinRequestStatus('pending');
      }
    };
    window.addEventListener('channel:join-request-submitted', handleJoinRequestSync);

    return () => {
      window.removeEventListener('channel:joined', handleSync);
      window.removeEventListener('channel:left', handleSync);
      window.removeEventListener('channel:invitation-accepted', handleSync);
      window.removeEventListener('channel:join-request-submitted', handleJoinRequestSync);
    };
  }, [channelId]);


  // Check if we should open manage modals from navigation state
  useEffect(() => {
    if (channel && userRole && !isLoading) {
      if (location.state?.openManageMembersModal && (userRole === "owner" || userRole === "admin")) {
        setShowManageMembersModal(true);
        navigate(location.pathname, { replace: true, state: null });
      } else if (location.state?.openManageJoinRequestsModal && (userRole === "owner" || userRole === "admin")) {
        setShowJoinRequestsModal(true);
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location.state, channel, userRole, isLoading, navigate]);

  const handlePostCreated = (newPost) => {
    if (!newPost) return;
    
    // Add new post at the beginning (newest first) and ensure sorting
    setPosts(prev => {
      // Check if post already exists to avoid duplicates
      const postId = newPost.id || newPost.post_id || newPost.postId;
      if (postId && prev.some((p) => {
        const existingId = p.id || p.post_id || p.postId;
        return existingId && Number(existingId) === Number(postId);
      })) {
        return prev; // Post already exists, don't add duplicate
      }
      
      const updatedPosts = [newPost, ...prev];
      // Sort by created_at in descending order (newest first)
      return updatedPosts.sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || 0);
        const dateB = new Date(b.created_at || b.createdAt || 0);
        return dateB - dateA; // Descending order (newest first)
      });
    });
    setChannel(prev => prev ? { ...prev, postCount: (prev.postCount || 0) + 1 } : null);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsPostModalOpen(true);
  };

  const handleUpdatePost = (postId, updatedData) => {
    try {
      // Hand off to background upload/update
      startUpdate(postId, updatedData);

      // Optimistic update
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          const currentId = p.id || p.post_id || p.postId;
          if (String(currentId) !== String(postId)) return p;

          const next = { ...p };
          if (typeof updatedData?.content === "string") {
            next.content = updatedData.content;
          }
          if (typeof updatedData?.privacy === "string") {
            next.privacy = updatedData.privacy;
          }
          if (Array.isArray(updatedData?.removedMediaIds) && Array.isArray(next.media)) {
            const removed = new Set(updatedData.removedMediaIds.map((id) => String(id)));
            next.media = next.media.filter((m) => !removed.has(String(m?.id)));
          }
          return next;
        })
      );

      // Close modal
      setIsPostModalOpen(false);
      setEditingPost(null);
    } catch (err) {
      console.error("Failed to initiate post update:", err);
    }
  };

  const handleDeletePost = (post) => {
    setPostToDelete(post);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!postToDelete) return;

    setIsDeleting(true);
    try {
      const postId = postToDelete.id || postToDelete.post_id || postToDelete.postId;
      await postsAPI.deletePost(postId);

      // Remove from local state
      setPosts((prevPosts) =>
        prevPosts.filter((p) => {
          const currentId = p.id || p.post_id || p.postId;
          return String(currentId) !== String(postId);
        })
      );

      // Update channel count
      setChannel((prev) =>
        prev ? { ...prev, postCount: Math.max(0, (prev.postCount || 0) - 1) } : null
      );
    } catch (err) {
      console.error("Failed to delete post:", err);
      alert(t("socialFeed.errors.deletePost") || "Failed to delete post");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setPostToDelete(null);
    }
  };

  const handleInviteMembers = () => {
    setShowInviteModal(true);
  };

  const handleSettings = () => {
    setShowSettings(true);
  };

  const handleSettingsUpdated = (updatedChannel) => {
    setChannel(updatedChannel);
    setShowSettings(false);
  };

  if (isLoading) {
    return <ChannelDetailSkeleton />;
  }

  if (!channel) {
    return (
      <div className="channel-detail-container">
        <div className="channel-detail-error">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <h2>{t("channelDetail.notFound.title", "Channel not found")}</h2>
          <p>
            {t(
              "channelDetail.notFound.body",
              "The channel you're looking for doesn't exist or has been removed."
            )}
          </p>
          <button
            className="channel-detail-back-btn"
            onClick={() => navigate(getBackPath())}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span>{t("common.back", "Back")}</span>
          </button>
        </div>
      </div>
    );
  }

  const handleJoinChannel = async () => {
    try {
      if (channel.isPublic) {
        await channelsAPI.joinChannel(channel.id);
        setIsJoined(true);
        // Trigger a full re-fetch to get member-only content (like posts)
        setRefreshTrigger(prev => prev + 1);
        
        // Dispatch event to refresh channels in sidebar
        window.dispatchEvent(new CustomEvent('channel:joined', {
          detail: { channelId: channel.id }
        }));
      } else if (joinRequestStatus === 'pending') {
        // Already pending, do nothing or show message
      } else {
        setShowChannelJoinRequestModal(true);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRequestJoinSent = (message) => {
    setJoinRequestStatus('pending');
    // Dispatch global event for other components (like sidebar)
    window.dispatchEvent(new CustomEvent('channel:join-request-submitted', {
      detail: { 
        channelId: channel.id,
        message 
      }
    }));
  };


  const hasManagePrivileges = userRole === "owner" || userRole === "admin";
  const pinnedPostIds = Array.isArray(channel.pinnedPostIds)
    ? channel.pinnedPostIds
    : [];
  const normalizePostId = (id) => {
    const numeric = Number(id);
    return Number.isNaN(numeric) ? id : numeric;
  };
  const pinnedPosts = Array.isArray(channel.pinnedPosts) && channel.pinnedPosts.length > 0
    ? channel.pinnedPosts
    : posts
      .filter((post) => {
        if (!post) return false;
        if (post.isPinned || post.is_pinned || post.pinned) return true;
        const postId = normalizePostId(post.id);
        return pinnedPostIds.some(
          (pinnedId) => normalizePostId(pinnedId) === postId
        );
      })
      .slice(0, 3);
  const recentMembers = Array.isArray(channel.membersPreview)
    ? channel.membersPreview
    : [];
  const channelRules = Array.isArray(channel.rules) ? channel.rules : [];
  const upcomingEvents = Array.isArray(channel.upcomingEvents)
    ? channel.upcomingEvents
    : [];

  const formatEventDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="channel-detail-container" style={{ '--user-type-color': userTypeColor }}>
      {!isJoined && channel ? (
        <>
          <ChannelHeader channel={channel} userRole={userRole} />
          <div className="channel-detail-join-prompt">
            <div className="join-prompt-content">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="3" x2="9" y2="21"></line>
              </svg>
              <h2>
                {t(
                  "channelDetail.joinPrompt.title",
                  "Join this channel to see posts"
                )}
              </h2>
              <p>{channel.description}</p>
              <button 
                className={`join-channel-btn ${joinRequestStatus === 'pending' ? 'pending-request' : ''}`} 
                onClick={handleJoinChannel}
                disabled={joinRequestStatus === 'pending'}
              >
                {channel.isPublic
                  ? t("channelDetail.joinPrompt.joinButton", "Join Channel")
                  : joinRequestStatus === 'pending'
                    ? t("channelDetail.joinPrompt.pendingButton", "Request Pending")
                    : t("channelDetail.joinPrompt.requestButton", "Request to Join")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>

      <button
        className="channel-detail-back-btn"
          onClick={() => navigate(getBackPath())}
          aria-label={fromProfile ? t("channelDetail.backToProfile", "Back to Profile") : t("common.back", "Back")}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
          <span>{fromProfile ? t("channelDetail.backToProfile", "Back to Profile") : t("common.back", "Back")}</span>
      </button>
      <div ref={headerContainerRef}>
        <ChannelHeader
          channel={channel}
          userRole={userRole}
          onOpenSidebarDrawer={() => setShowSidebarDrawer(true)}
          onSettings={() => setShowSettings(true)}
          onManageMembers={() => setShowManageMembersModal(true)}
          onInviteMembers={() => setShowInviteModal(true)}
          onRules={() => setShowRulesModal(true)}
          onManageJoinRequests={() => setShowJoinRequestsModal(true)}
        />
      </div>

      <div className="channel-detail-content">
        <div className="channel-detail-main">
          {isJoined && (
            <ChannelPostCreation
              channel={channel}
              onPostCreated={handlePostCreated}
            />
          )}

          <ChannelPostFeed
            posts={posts}
            channel={channel}
            userRole={userRole}
            onPostsUpdate={setPosts}
            onEditPost={handleEditPost}
            onDeletePost={handleDeletePost}
            onChannelUpdated={({ post, isPinned }) =>
              setChannel(prev => {
                if (!prev) return prev;
                const postId = normalizeId(post.id);
                
                // Incremental update of IDs
                const newPinnedPostIds = isPinned
                  ? [...(prev.pinnedPostIds || []), postId]
                  : (prev.pinnedPostIds || []).filter(id => normalizeId(id) !== postId);

                // Incremental update of post objects (with deduplication)
                const newPinnedPosts = isPinned
                  ? [post, ...(prev.pinnedPosts || [])].filter((p, index, self) => 
                      index === self.findIndex((t) => normalizeId(t.id) === normalizeId(p.id))
                    )
                  : (prev.pinnedPosts || []).filter(p => normalizeId(p.id) !== postId);

                return {
                  ...prev,
                  pinnedPostIds: newPinnedPostIds,
                  pinnedPosts: newPinnedPosts
                };
              })
            }
          />
        </div>

        <aside
          className={`channel-detail-sidebar${i18n.language?.startsWith("ar") ? " rtl" : ""
            }`}
        >
          {/* Sticky Mini Header */}
          {isHeaderScrolled && channel && (
            <div className="channel-side-card channel-sticky-mini-header">
              <div className="channel-sticky-info">
                <img 
                  src={channel.profileImage || "/images/social/pic-placeholder.png"} 
                  alt={channel.name}
                  className="channel-sticky-avatar"
                />
                <div className="channel-sticky-text">
                  <h4 className="channel-sticky-name">{channel.name}</h4>
                  <p className="channel-sticky-meta">
                    {t("channelHeader.membersCount", "{{count}} members", {
                      count: channel.memberCount.toLocaleString(),
                    })}
                  </p>
                </div>
              </div>
              <div className="channel-sticky-actions">
                {/* Settings */}
                {userRole === "owner" && (
                  <button 
                    className="channel-sticky-btn"
                    onClick={() => setShowSettings(true)}
                    title={t("channelDetail.sidebar.actions.settings.title", "Community settings")}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                )}

                {/* Members */}
                {userRole && (userRole === "member" || hasManagePrivileges) && (
                  <button 
                    className="channel-sticky-btn"
                    onClick={() => setShowManageMembersModal(true)}
                    title={hasManagePrivileges 
                      ? t("channelDetail.sidebar.actions.members.title", "Manage members")
                      : t("channelDetail.sidebar.actions.members.viewTitle", "View members")
                    }
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </button>
                )}

                {/* Invite */}
                {hasManagePrivileges && (
                  <button 
                    className="channel-sticky-btn"
                    onClick={() => setShowInviteModal(true)}
                    title={t("channelDetail.sidebar.actions.invite.title", "Invite people")}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                )}

                {/* Join Requests */}
                {hasManagePrivileges && (
                  <button 
                    className="channel-sticky-btn"
                    onClick={() => setShowJoinRequestsModal(true)}
                    title={t("channelHeader.manageJoinRequests", "Manage join requests")}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <path d="M20 8v6"></path>
                      <path d="M23 11h-6"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          <section className="channel-side-card channel-side-card--rules">
            <header className="channel-side-card-header channel-side-rules-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <h3>
                {t(
                  "channelDetail.sidebar.rules.title",
                  "Channel guidelines"
                )}
              </h3>
              {userRole === "owner" && (
                <button
                  type="button"
                  className="channel-side-rules-edit"
                  onClick={() => setShowRulesModal(true)}
                  aria-label={t("common.edit", "Edit")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              )}
            </header>
            {channelRules.length > 0 ? (
              <ol className="channel-side-rule-list">
                {channelRules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ol>
            ) : (
              hasManagePrivileges && (
                <p className="channel-side-empty">
                  {t(
                    "channelDetail.sidebar.rules.empty",
                    "Share a few expectations to help new members feel at home."
                  )}
                </p>
              )
            )}
          </section>

          <section className="channel-side-card channel-side-card--pinned">
            <header className="channel-side-card-header">
              <Pin width="16" height="16" style={{ transform: "rotate(45deg)" }} />
              <h3>
                {t("channelDetail.sidebar.pinboard.title", "Pinboard")}
              </h3>
              {pinnedPosts.length > 0 && (
                <span className="channel-side-count">{pinnedPosts.length}</span>
              )}
            </header>
            {pinnedPosts.length > 0 ? (
              <div className="channel-side-pinned-list">
                {pinnedPosts.map((post) => {
                  const snippet =
                    post.title ||
                    (post.content
                      ? post.content.replace(/\s+/g, " ").trim().slice(0, 120)
                      : t(
                        "channelDetail.sidebar.pinboard.defaultSnippet",
                        "Pinned update"
                      ));

                  const postType =
                    post.post_type ||
                    post.normalizedType ||
                    (post.type !== "post" ? post.type : null);

                  return (
                    <article
                      key={post.id || snippet}
                      className="channel-side-pinned-item"
                    >
                      <div className="channel-side-pinned-badge">
                        <span>
                          {t(
                            "channelDetail.sidebar.pinboard.badge",
                            "Pinned"
                          )}
                        </span>
                        {postType && <em>{postType}</em>}
                      </div>
                      <p title={snippet}>{snippet}</p>
                    </article>
                  );
                })}
              </div>
            ) : (
              hasManagePrivileges && (
                <p className="channel-side-empty">
                  {t(
                    "channelDetail.sidebar.pinboard.empty",
                    "No pinned posts yet. Mark important updates from the post menu."
                  )}
                </p>
              )
            )}
          </section>

          <section className="channel-side-card channel-side-card--events">
            <header className="channel-side-card-header">
              <Calendar width="16" height="16" />
              <h3>
                {t(
                  "channelDetail.sidebar.events.title",
                  "Upcoming events"
                )}
              </h3>
              {userRole === "owner" && (
                <button
                  type="button"
                  className="channel-side-link"
                  onClick={() => setShowEventsModal(true)}
                >
                  {t(
                    "channelDetail.sidebar.events.addButton",
                    "Add event"
                  )}
                </button>
              )}
            </header>
            {upcomingEvents.length > 0 ? (
              <div className="channel-side-event-list">
                {upcomingEvents.map((event) => (
                  <div className="channel-side-event" key={event.id || event.title}>
                    <div className="channel-side-event-date">
                      {formatEventDate(event.date) || "TBA"}
                    </div>
                    <div className="channel-side-event-info">
                      <span>{event.title}</span>
                      {event.location && <small>{event.location}</small>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              hasManagePrivileges && (
                <p className="channel-side-empty">
                  {t(
                    "channelDetail.sidebar.events.empty",
                    "Plan a live session or office hours and it will show up here."
                  )}
                </p>
              )
            )}
          </section>
        </aside>
      </div>

      {showSettings && userRole === "owner" && (
        <ChannelSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          channel={channel}
          userRole={userRole}
          onChannelUpdated={handleSettingsUpdated}
        />
      )}

      {showInviteModal && (
        <InviteMembersModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          existingMemberIds={[
            ...(channel.members || []),
            ...(channel.admins || []),
            channel.owner
          ]
            .map(m => m?.id || m?.user_id)
            .filter(Boolean)}
        />
      )}

      {showManageMembersModal && (
        <ManageMembersModal
          isOpen={showManageMembersModal}
          onClose={() => setShowManageMembersModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          userRole={userRole}
        />
      )}

      {showRulesModal && userRole === "owner" && (
        <ChannelRulesModal
          isOpen={showRulesModal}
          onClose={() => setShowRulesModal(false)}
          channelId={channel.id}
          initialRules={channelRules}
          onRulesUpdated={(nextRules) =>
            setChannel((prev) =>
              prev ? { ...prev, rules: nextRules } : prev
            )
          }
        />
      )}

      {showEventsModal && userRole === "owner" && (
        <ChannelEventsModal
          isOpen={showEventsModal}
          onClose={() => setShowEventsModal(false)}
          channelId={channel.id}
          initialEvents={upcomingEvents}
          onEventsUpdated={(nextEvents) =>
            setChannel((prev) =>
              prev ? { ...prev, upcomingEvents: nextEvents } : prev
            )
          }
        />
      )}

      {showJoinRequestsModal && channel && (
        <ManageJoinRequestsModal
          isOpen={showJoinRequestsModal}
          onClose={() => setShowJoinRequestsModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          userRole={userRole}
        />
      )}

      {showChannelJoinRequestModal && channel && (
        <JoinRequestModal
          isOpen={showChannelJoinRequestModal}
          onClose={() => setShowChannelJoinRequestModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          onRequestSent={handleRequestJoinSent}
        />
      )}
        </>
      )}

      {/* Sidebar Drawer for Mobile (425px and below) */}
      {showSidebarDrawer && (
        <>
          <div
            className="channel-sidebar-drawer-overlay"
            onClick={() => setShowSidebarDrawer(false)}
          />
          <div className={`channel-sidebar-drawer${i18n.language?.startsWith("ar") ? " rtl" : ""}`}>
            <div className="channel-sidebar-drawer-header">
              <h2>{t("channelDetail.sidebar.title", "Channel Information")}</h2>
              <button
                className="channel-sidebar-drawer-close"
                onClick={() => setShowSidebarDrawer(false)}
                aria-label={t("common.close", "Close")}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="channel-sidebar-drawer-content">
              {hasManagePrivileges && (
                <section className="channel-side-card channel-side-card--actions">
                  <header className="channel-side-card-header">
                    <h3>
                      {t("channelDetail.sidebar.actions.title", "Quick actions")}
                    </h3>
                    <span>
                      {t(
                        "channelDetail.sidebar.actions.subtitle",
                        "Keep the community flowing"
                      )}
                    </span>
                  </header>
                  <div className="channel-side-actions-grid">
                    {userRole === "owner" && (
                      <button
                        type="button"
                        className="channel-side-action"
                        onClick={() => {
                          handleSettings();
                          setShowSidebarDrawer(false);
                        }}
                      >
                        <div className="channel-side-action-icon settings">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                          </svg>
                        </div>
                        <div className="channel-side-action-copy">
                          <span>
                            {t(
                              "channelDetail.sidebar.actions.settings.title",
                              "Channel settings"
                            )}
                          </span>
                          <small>
                            {t(
                              "channelDetail.sidebar.actions.settings.subtitle",
                              "Update name, imagery, privacy"
                            )}
                          </small>
                        </div>
                      </button>
                    )}
                    <button
                      type="button"
                      className="channel-side-action"
                      onClick={() => {
                        setShowManageMembersModal(true);
                        setShowSidebarDrawer(false);
                      }}
                    >
                      <div className="channel-side-action-icon members">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <div className="channel-side-action-copy">
                        <span>
                          {t(
                            "channelDetail.sidebar.actions.members.title",
                            "Manage members"
                          )}
                        </span>
                        <small>
                          {t(
                            "channelDetail.sidebar.actions.members.subtitle",
                            "Approve requests, assign roles"
                          )}
                        </small>
                      </div>
                    </button>
                    {hasManagePrivileges && (
                      <button
                        type="button"
                        className="channel-side-action"
                        onClick={() => {
                          handleInviteMembers();
                          setShowSidebarDrawer(false);
                        }}
                      >
                        <div className="channel-side-action-icon invite">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                          </svg>
                        </div>
                        <div className="channel-side-action-copy">
                          <span>
                            {t(
                              "channelDetail.sidebar.actions.invite.title",
                              "Invite people"
                            )}
                          </span>
                          <small>
                            {t(
                              "channelDetail.sidebar.actions.invite.subtitle",
                              "Share invites or add teammates"
                            )}
                          </small>
                        </div>
                      </button>
                    )}
                  </div>
                </section>
              )}

              <section className="channel-side-card channel-side-card--rules">
                <header className="channel-side-card-header channel-side-rules-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <h3>
                    {t(
                      "channelDetail.sidebar.rules.title",
                      "Channel guidelines"
                    )}
                  </h3>
                  {userRole === "owner" && (
                    <button
                      type="button"
                      className="channel-side-rules-edit"
                      onClick={() => {
                        setShowRulesModal(true);
                        setShowSidebarDrawer(false);
                      }}
                      aria-label={t("common.edit", "Edit")}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121(0) 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  )}
                </header>
                {channelRules.length > 0 ? (
                  <ol className="channel-side-rule-list">
                    {channelRules.map((rule, index) => (
                      <li key={index}>{rule}</li>
                    ))}
                  </ol>
                ) : (
                  hasManagePrivileges && (
                    <p className="channel-side-empty">
                      {t(
                        "channelDetail.sidebar.rules.empty",
                        "Share a few expectations to help new members feel at home."
                      )}
                    </p>
                  )
                )}
              </section>

              <section className="channel-side-card channel-side-card--pinned">
                <header className="channel-side-card-header">
                  <h3>
                    {t("channelDetail.sidebar.pinboard.title", "Pinboard")}
                  </h3>
                  {pinnedPosts.length > 0 && (
                    <span className="channel-side-count">{pinnedPosts.length}</span>
                  )}
                </header>
                {pinnedPosts.length > 0 ? (
                  <div className="channel-side-pinned-list">
                    {pinnedPosts.map((post) => {
                      const snippet =
                        post.title ||
                        (post.content
                          ? post.content.replace(/\s+/g, " ").trim().slice(0, 120)
                          : t(
                            "channelDetail.sidebar.pinboard.defaultSnippet",
                            "Pinned update"
                          ));

                      const postType =
                        post.post_type ||
                        post.normalizedType ||
                        (post.type !== "post" ? post.type : null);

                      return (
                        <article
                          key={post.id || snippet}
                          className="channel-side-pinned-item"
                        >
                          <div className="channel-side-pinned-badge">
                            <span>
                              {t(
                                "channelDetail.sidebar.pinboard.badge",
                                "Pinned"
                              )}
                            </span>
                            {postType && <em>{postType}</em>}
                          </div>
                          <p title={snippet}>{snippet}</p>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  hasManagePrivileges && (
                    <p className="channel-side-empty">
                      {t(
                        "channelDetail.sidebar.pinboard.empty",
                        "No pinned posts yet. Mark important updates from the post menu."
                      )}
                    </p>
                  )
                )}
              </section>

              <section className="channel-side-card channel-side-card--events">
                <header className="channel-side-card-header">
                  <h3>
                    {t(
                      "channelDetail.sidebar.events.title",
                      "Upcoming events"
                    )}
                  </h3>
                  {userRole === "owner" && (
                    <button
                      type="button"
                      className="channel-side-link"
                      onClick={() => {
                        setShowEventsModal(true);
                        setShowSidebarDrawer(false);
                      }}
                    >
                      {t(
                        "channelDetail.sidebar.events.addButton",
                        "Add event"
                      )}
                    </button>
                  )}
                </header>
                {upcomingEvents.length > 0 ? (
                  <div className="channel-side-event-list">
                    {upcomingEvents.map((event) => (
                      <div className="channel-side-event" key={event.id || event.title}>
                        <div className="channel-side-event-date">
                          {formatEventDate(event.date) || "TBA"}
                        </div>
                        <div className="channel-side-event-info">
                          <span>{event.title}</span>
                          {event.location && <small>{event.location}</small>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  hasManagePrivileges && (
                    <p className="channel-side-empty">
                      {t(
                        "channelDetail.sidebar.events.empty",
                        "Plan a live session or office hours and it will show up here."
                      )}
                    </p>
                  )
                )}
              </section>
            </div>
          </div>
        </>
      )}
      {/* Transpiled Modals */}
      <PostModal
        isOpen={isPostModalOpen}
        onClose={() => {
          setIsPostModalOpen(false);
          setEditingPost(null);
        }}
        mode="edit"
        editingPost={editingPost}
        currentUser={user}
        onUpdatePost={handleUpdatePost}
        hidePrivacyDropdown={true}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPostToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title={t("socialFeed.deleteModal.title")}
        message={t("socialFeed.deleteModal.message")}
        confirmText={t("socialFeed.deleteModal.confirm")}
        cancelText={t("socialFeed.deleteModal.cancel")}
      />
      {showJoinRequestsModal && channel && (
        <ManageJoinRequestsModal
          isOpen={showJoinRequestsModal}
          onClose={() => setShowJoinRequestsModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          userRole={userRole}
        />
      )}

      {showChannelJoinRequestModal && channel && (
        <JoinRequestModal
          isOpen={showChannelJoinRequestModal}
          onClose={() => setShowChannelJoinRequestModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          onRequestSent={handleRequestJoinSent}
        />
      )}
    </div>
  );
};


export default ChannelDetail;

